import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import type { WarehouseItem } from "./WarehouseLanding.js";

type WarehouseLog = {
  id: string;
  itemId: string;
  delta: number;
  reason: string;
  refId: string | null;
  note: string | null;
  actorChatId: string | null;
  createdAt: string;
};

const REASON_LABEL: Record<string, string> = {
  shop_order: "Заказ",
  manual_in: "Приход",
  manual_out: "Расход",
  correction: "Коррекция",
  restock: "Пополнение",
};

interface StockHistoryTabProps {
  items: WarehouseItem[];
}

export function StockHistoryTab({ items }: StockHistoryTabProps) {
  const [selectedItemId, setSelectedItemId] = useState<string>(
    items[0]?.id ?? "",
  );

  useEffect(() => {
    if (!selectedItemId && items.length > 0) {
      setSelectedItemId(items[0]!.id);
    }
  }, [items, selectedItemId]);

  const { data: logs = [], isLoading } = useQuery<WarehouseLog[]>({
    queryKey: ["warehouse-log", selectedItemId],
    queryFn: () =>
      api<WarehouseLog[]>(
        `/admin/warehouse/items/${selectedItemId}/log?limit=100`,
      ),
    enabled: !!selectedItemId,
  });

  const selectedItem = items.find((i) => i.id === selectedItemId);

  return (
    <div>
      <div className="toolbar" style={{ marginBottom: 0 }}>
        <select
          className="input-admin"
          value={selectedItemId}
          onChange={(e) => setSelectedItemId(e.target.value)}
          style={{ minWidth: 200 }}
          aria-label="Фильтр по позиции"
        >
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </select>
      </div>
      <div className="table-wrap">
        <table className="data">
          <thead>
            <tr>
              <th>Дата</th>
              <th>Операция</th>
              <th className="num">Δ</th>
              <th>Примечание</th>
              <th>Ссылка</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} style={{ padding: 40, textAlign: "center" }}>
                  Загрузка…
                </td>
              </tr>
            )}
            {!isLoading && logs.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 40, textAlign: "center" }}>
                  Записей пока нет
                </td>
              </tr>
            )}
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="muted" style={{ fontSize: 12 }}>
                  {new Date(log.createdAt).toLocaleString("ru-RU", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </td>
                <td>{REASON_LABEL[log.reason] ?? log.reason}</td>
                <td
                  className="num tnum bold"
                  style={{
                    color:
                      log.delta > 0
                        ? "var(--mnp-teal-600, #319795)"
                        : "var(--mnp-red, #e53e3e)",
                  }}
                >
                  {log.delta > 0 ? `+${log.delta}` : log.delta}{" "}
                  {selectedItem?.unit}
                </td>
                <td className="muted">{log.note ?? "—"}</td>
                <td className="muted mono" style={{ fontSize: 11 }}>
                  {log.refId ?? "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
